import './messageHandlers/register';
import { createAction, createReducer } from '@reduxjs/toolkit';
import { formatSinceTime, localTime, ncFileStatuses } from '@helpers';
import { productionBatchStatistics } from '@reducers/statistics';
import { setMachines, setSelectedMachine } from '@reducers/machines';
import { backendCall, createApiCall } from '@reducers/utils';
import type { RootState, AppThunk } from '@store/types';
import { getDemoBatch } from '@components/tours/helpers/demoDataRegistry';
import type { CorrectQuantityRequestDto } from '@api/generated/models/correctQuantityRequestDto';

import { type BatchState, type BatchData, type BatchSquareSize, type ProductionBatchStatisticsResponse, QuantityCorrectionReasons } from './types';

// Re-export types for consumers
export type { BatchState, BatchData, BatchForHover, BatchSquareSize } from './types';

const initState: BatchState = {
  batchList: [],
  batch: { data: null, wasLoaded: false },
  batchId: null,
  batchLoadedTime: '',
  batchTileHeight: 0,
  batchSquareSize: { height: 0, width: 0 },
  batchListContainerHeight: 0,
  batchListYPosition: 0,
  batchForHover: {
    id: null,
    shouldHover: false,
  },
  batchListWidth: 0,
};

export const selectBatchList = (state: RootState) => state.batch.batchList;
export const selectSingleBatch = (state: RootState) => state.batch.batch.data;
export const selectSingleBatchData = (state: RootState) => state.batch.batch;
export const selectBatchForHover = (state: RootState) => state.batch.batchForHover;
export const selectBatchId = (state: RootState) => state.batch.batchId;

export const setBatchListAction = createAction<BatchData[]>('setBatchListAction');
export const setBatchAction = createAction<BatchData | null>('setBatchAction');
export const setBatchTileHeightAction = createAction<number>('setBatchTileHeightAction');
export const setBatchListContainerHeightAction = createAction<number>('setBatchListContainerHeightAction');
export const setBatchListYPositionAction = createAction<number>('setBatchListYPositionAction');
export const setBatchLoadedTimeAction = createAction<string>('setBatchLoadedTimeAction');
export const setBatchIdForHoverAction = createAction<number | null>('setBatchIdForHoverAction');
export const setShouldHoverBatchAction = createAction<boolean>('setShouldHoverBatchAction');
export const setBatchListWidthAction = createAction<number>('setBatchListWidthAction');
export const setBatchSquareSizeAction = createAction<BatchSquareSize>('setBatchSquareSizeAction');
export const setBatchQuantityReady = createAction<number>('setBatchQuantityReady');
export const setBatchIdAction = createAction<number | null>('setBatchIdAction');

export const setBatchLoadedTime =
  (loadedTime: string): AppThunk =>
  (dispatch) => {
    dispatch(setBatchLoadedTimeAction(loadedTime));
  };

export const setBatchListWidth =
  (width: number): AppThunk =>
  (dispatch) => {
    if (width) {
      dispatch(setBatchListWidthAction(width));
    }
  };

export const setBatchIdForHover =
  (id: number | null): AppThunk =>
  (dispatch) => {
    dispatch(setBatchIdForHoverAction(id));
  };

export const setShouldHoverBatch =
  (state: boolean): AppThunk =>
  (dispatch) => {
    dispatch(setShouldHoverBatchAction(state));
  };

export const setBatchList =
  (batchList: BatchData[]): AppThunk =>
  (dispatch) => {
    dispatch(setBatchListAction(batchList));
  };

export const setBatch =
  (batch: BatchData | null): AppThunk =>
  (dispatch) => {
    dispatch(setBatchAction(batch));
  };

export const setBatchListContainerHeight =
  (height: number): AppThunk =>
  (dispatch) => {
    dispatch(setBatchListContainerHeightAction(height));
  };

export const setBatchSquareSize =
  (size: BatchSquareSize): AppThunk =>
  (dispatch) => {
    dispatch(setBatchSquareSizeAction(size));
  };

export const setBatchListYPosition =
  (y: number): AppThunk =>
  (dispatch) => {
    dispatch(setBatchListYPositionAction(y));
  };

export const setBatchTileHeight =
  (height: number): AppThunk =>
  (dispatch, getState) => {
    const currentHeight = getState().batch.batchTileHeight;
    if (currentHeight === height) return;
    dispatch(setBatchTileHeightAction(height));
  };

export const updateBatchLocally = (batchForUpdate: BatchData) => {
  const oldLinkStatus = batchForUpdate?.linkStatus;
  const newLinkStatus = ['SingleLink', 'MultiLink'].includes(oldLinkStatus!) ? 'MultiLink' : 'SingleLink';

  return { ...batchForUpdate, ncFileStatus: ncFileStatuses.Linked, linkStatus: newLinkStatus };
};

export const updateSingleBatchLocally =
  (batchId: number): AppThunk =>
  (dispatch, getState) => {
    const batchForUpdate = getState().batch.batch.data;
    if (batchForUpdate === null || batchForUpdate?.productionBatchId !== batchId) return;
    const updatedBatch = updateBatchLocally(batchForUpdate);
    dispatch(setBatch(updatedBatch));
  };

export const updateBatchListLocally =
  (batchId: number): AppThunk =>
  (dispatch, getState) => {
    const batchList = getState().batch.batchList;
    const batchForUpdate = batchList.find((b) => b.productionBatchId === batchId);
    if (!batchForUpdate) return;
    const updatedBatch = updateBatchLocally(batchForUpdate);
    const updatedBatchList = batchList.map((b) => (b.productionBatchId === batchId ? updatedBatch : b));
    dispatch(setBatchListAction(updatedBatchList));
  };

export const updateBatchListWithFetchCall =
  (updatedElementFromServer: ProductionBatchStatisticsResponse): AppThunk =>
  (dispatch, getState) => {
    const batchList = getState().batch.batchList;
    const batchForUpdate = batchList.find((b) => b.productionBatchId === updatedElementFromServer?.statistics?.productionBatchId);
    if (!batchForUpdate) return;
    const updatedBatchListWithDataFromServer = batchList.map((b) =>
      b.productionBatchId === updatedElementFromServer?.statistics?.productionBatchId ? { ...b, ...updatedElementFromServer.statistics } : b
    );

    dispatch(setBatchListAction(updatedBatchListWithDataFromServer));
  };

export const updateBatchWithFetchCall =
  (updatedElementFromServer: ProductionBatchStatisticsResponse): AppThunk =>
  (dispatch, getState) => {
    const batchForUpdate = getState().batch.batch.data;
    const selectedMachine = getState()?.machines?.selectedItem;
    const machines = getState()?.machines?.machines;

    if (!batchForUpdate || batchForUpdate?.productionBatchId !== updatedElementFromServer?.statistics?.productionBatchId) return;
    dispatch(setBatch(updatedElementFromServer?.statistics));

    if (
      selectedMachine &&
      selectedMachine?.machineStatus?.productionBatchId === updatedElementFromServer?.statistics?.productionBatchId &&
      selectedMachine?.machineStatus?.partDetails?.partId == null
    ) {
      const updatedMachine = {
        ...selectedMachine,
        machineStatus: {
          ...selectedMachine?.machineStatus,
          partDetails: { ...selectedMachine?.machineStatus?.partDetails, partId: updatedElementFromServer?.statistics?.partId },
        },
      };
      dispatch(setSelectedMachine(updatedMachine));
      dispatch(setMachines(machines.map((m) => (m.machineId === selectedMachine.machineId ? updatedMachine : m))));
    }
  };

export const loadSingleBatch =
  (batchId: number, signal?: AbortSignal): AppThunk<Promise<ProductionBatchStatisticsResponse | undefined>> =>
  async (dispatch) => {
    if (!batchId) return;

    const demoBatchData = getDemoBatch(batchId);
    if (demoBatchData) {
      dispatch(setBatch(demoBatchData));
      return { statistics: demoBatchData };
    }

    const batchStatistic = await dispatch(productionBatchStatistics({ productionBatchId: batchId }, signal));

    if (batchStatistic?.statistics) {
      dispatch(setBatch(batchStatistic.statistics));
    }
    dispatch(setBatchLoadedTime(formatSinceTime(localTime().format()).toLowerCase()));

    return batchStatistic;
  };

export const correctQuantity =
  (number: number): AppThunk<Promise<void | undefined>> =>
  async (dispatch, getState) => {
    const {
      batch: { batch },
    } = getState();

    dispatch(setBatchQuantityReady(batch.data!.quantityReady! + number));

    const params: CorrectQuantityRequestDto = {
      productionBatchId: batch.data?.productionBatchId,
      reason: QuantityCorrectionReasons.Miscount,
      reasonDetails: null,
      corrections: [
        {
          machineEventSequenceNumber: null,
          quantityDelta: number,
        },
      ],
    };

    return await dispatch(backendCall(createApiCall((api) => api.productionInformation.correctQuantity, { params })));
  };

export const setBatchId =
  (batchId: number | null): AppThunk<Promise<void>> =>
  async (dispatch) => {
    dispatch(setBatchIdAction(batchId));
  };

export default createReducer<BatchState>(initState, {
  [setBatchListAction.type]: (state, action: { payload: BatchData[] }) => {
    return { ...state, batchList: action.payload };
  },
  [setBatchAction.type]: (state, action: { payload: BatchData | null }) => {
    return { ...state, batch: { data: action.payload, wasLoaded: true } };
  },
  [setBatchQuantityReady.type]: (state, action: { payload: number }) => {
    return {
      ...state,
      batch: {
        ...state.batch,
        data: {
          ...state.batch.data,
          quantityReady: action.payload,
        },
      },
    };
  },
  [setBatchTileHeightAction.type]: (state, action: { payload: number }) => {
    return { ...state, batchTileHeight: action.payload };
  },
  [setBatchListContainerHeightAction.type]: (state, action: { payload: number }) => {
    return { ...state, batchListContainerHeight: action.payload };
  },
  [setBatchListYPositionAction.type]: (state, action: { payload: number }) => {
    return { ...state, batchListYPosition: action.payload };
  },
  [setBatchLoadedTimeAction.type]: (state, action: { payload: string }) => {
    return { ...state, batchLoadedTime: action.payload };
  },
  [setBatchIdForHoverAction.type]: (state, action: { payload: number | null }) => {
    return { ...state, batchForHover: { ...state.batchForHover, id: action.payload } };
  },
  [setShouldHoverBatchAction.type]: (state, action: { payload: boolean }) => {
    return { ...state, batchForHover: { ...state.batchForHover, shouldHover: action.payload } };
  },
  [setBatchListWidthAction.type]: (state, action: { payload: number }) => {
    return { ...state, batchListWidth: action.payload };
  },
  [setBatchSquareSizeAction.type]: (state, action: { payload: BatchSquareSize }) => {
    return { ...state, batchSquareSize: action.payload };
  },
  [setBatchIdAction.type]: (state, action: { payload: number | null }) => {
    return { ...state, batchId: action.payload };
  },
});
